/**
 * Fertility Widget
 *
 * Manages three independent layers of state:
 *
 * 1. Widget open/closed → localStorage key `fertilityWidgetOpen`
 *    Persists whether the panel is expanded or collapsed across page loads.
 *
 * 2. Form page position → sessionStorage key `fertilityWidgetPage`
 *    Listens to Formie's native `onFormiePageToggle` event and saves the
 *    current page ID + index. When the form next initialises (same page or
 *    after navigation), it reads this key and jumps straight to the saved page
 *    via Formie's own `formTheme.setPage()` API.
 *    Cleared automatically on successful submission via `onAfterFormieSubmit`.
 *    Cleared automatically when the browser is closed (sessionStorage).
 *
 * 3. Field values → sessionStorage key `fertilityWidgetFields`
 *    Saves every field value on each `input` / `change` event so that data
 *    is never lost when the user navigates between the widget and the
 *    full-page form, or refreshes the page.
 *    Restored when the form initialises via `onFormieThemeReady`.
 *    Cleared automatically on successful submission via `onAfterFormieSubmit`.
 *    Cleared automatically when the browser is closed (sessionStorage).
 */

const OPEN_KEY   = 'fertilityWidgetOpen'
const PAGE_KEY   = 'fertilityWidgetPage'
const FIELD_KEY  = 'fertilityWidgetFields'

// ── Alpine component ──────────────────────────────────────────────────────────

document.addEventListener('alpine:init', () => {
    Alpine.data('fertilityWidget', () => ({
        isOpen: false,

        init() {
            this.isOpen = localStorage.getItem(OPEN_KEY) === 'true'

            // Prevent the browser from scrolling the page when an element inside
            // the fixed widget receives focus (e.g. Formie auto-focuses an input).
            this.$el.addEventListener('focusin', () => {
                const y = window.scrollY
                requestAnimationFrame(() => {
                    if (window.scrollY !== y) window.scrollTo({ top: y, behavior: 'instant' })
                })
            }, true)
        },

        open() {
            const y = window.scrollY
            this.isOpen = true
            localStorage.setItem(OPEN_KEY, 'true')
            // Restore scroll position after x-collapse reveals the form and any
            // auto-focus causes the browser to scroll.
            requestAnimationFrame(() => {
                requestAnimationFrame(() => window.scrollTo({ top: y, behavior: 'instant' }))
            })
        },

        close() {
            this.isOpen = false
            localStorage.setItem(OPEN_KEY, 'false')
        },

        toggle() {
            this.isOpen ? this.close() : this.open()
        },

        // Navigate to the full-page form without triggering the browser's
        // "unsaved changes" warning. Formie compares savedFormHash to the
        // current hash on beforeunload — syncing them marks the form as clean.
        // Field values are already continuously saved to localStorage, so
        // no data is lost.
        expandToFullPage(url) {
            const $form = document.querySelector('.fui-form')
            if ($form && $form.formTheme && typeof $form.formTheme.hashForm === 'function') {
                $form.formTheme.savedFormHash = $form.formTheme.hashForm()
            }
            window.location.href = url
        },
    }))
})

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Serialise all named inputs in the form to a plain object and persist to
 * localStorage. Handles text, select, textarea, radio, and checkbox inputs.
 */
function saveFields($form) {
    const data = {}

    $form.querySelectorAll('[name]').forEach((input) => {
        const { name, type, value, checked } = input

        if (type === 'radio') {
            if (checked) data[name] = value
        } else if (type === 'checkbox') {
            if (!(name in data)) data[name] = []
            if (checked) data[name].push(value)
        } else {
            data[name] = value
        }
    })

    sessionStorage.setItem(FIELD_KEY, JSON.stringify(data))

    // Since field values are persisted to localStorage, data is safe — tell
    // Formie's beforeunload handler there are no unsaved changes so the
    // "Changes may not be saved" browser dialog never appears.
    if ($form.formTheme && typeof $form.formTheme.hashForm === 'function') {
        $form.formTheme.savedFormHash = $form.formTheme.hashForm()
    }
}

/**
 * Read saved field values from localStorage and apply them back to the form.
 * After setting values, dispatch a `change` event on each input so Formie's
 * conditional-logic engine re-evaluates visibility rules.
 */
function restoreFields($form) {
    const saved = JSON.parse(sessionStorage.getItem(FIELD_KEY) || 'null')
    if (!saved) return

    $form.querySelectorAll('[name]').forEach((input) => {
        const { name, type } = input
        if (!(name in saved)) return

        const savedValue = saved[name]

        if (type === 'radio') {
            input.checked = input.value === savedValue
        } else if (type === 'checkbox') {
            input.checked = Array.isArray(savedValue)
                ? savedValue.includes(input.value)
                : input.value === savedValue
        } else {
            input.value = savedValue
        }

        input.dispatchEvent(new Event('change', { bubbles: true }))
    })
}

// ── Formie state tracking ─────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
    // Formie fires `onFormieThemeReady` (bubbles=true) on the <form> element
    // once its JS theme is initialised. We catch it at the document level.
    document.addEventListener('onFormieThemeReady', (e) => {
        const $form = e.target
        const formTheme = $form.formTheme

        if (!formTheme) return

        // ── Restore field values ──────────────────────────────────────────────
        // Done before page restore so fields are populated on the correct page.
        restoreFields($form)

        // ── Restore saved page ────────────────────────────────────────────────
        const savedPage = JSON.parse(sessionStorage.getItem(PAGE_KEY) || 'null')

        if (savedPage && savedPage.pageId) {
            // Defer by one tick so Formie finishes its own ready logic first
            setTimeout(() => {
                formTheme.setPage({ pageIndex: savedPage.pageIndex, pageId: savedPage.pageId })
            }, 0)
        }

        // ── Autosave field values on every change ─────────────────────────────
        $form.addEventListener('input',  () => saveFields($form))
        $form.addEventListener('change', () => saveFields($form))

        // ── Save page index whenever the user advances or goes back ───────────
        $form.addEventListener('onFormiePageToggle', (e) => {
            const { nextPageId, nextPageIndex } = e.detail.data || {}

            if (nextPageId !== undefined) {
                sessionStorage.setItem(PAGE_KEY, JSON.stringify({
                    pageId: nextPageId,
                    pageIndex: nextPageIndex,
                }))
            }
        })

        // ── Clear all saved state on successful submission ────────────────────
        $form.addEventListener('onAfterFormieSubmit', () => {
            sessionStorage.removeItem(PAGE_KEY)
            sessionStorage.removeItem(FIELD_KEY)
        })
    })
})
